import React, { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { Button, Paragraph } from '~elements'
import { ButtonProps, RichTextType } from '~types'
import styles from './NotificationBanner.module.scss'
import CloseIcon from '/public/assets/svg/close-thick.svg'
import ChevronDown from '/public/assets/svg/chevron_down.svg'
import { useWindowSize } from '~utils/hooks'

interface NotificationBannerProps {
  copy: RichTextType
  bannerText: RichTextType
  ctaPrimary?: ButtonProps
  shouldExpand?: boolean
}

const NotificationBanner = ({
  copy: expandedDescription,
  ctaPrimary: expandedCta,
  bannerText: title,
  shouldExpand
}: NotificationBannerProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const expendedDescriptionHasValue = expandedDescription?.html && expandedDescription.html !== '<p></p>'
  const shouldExpandAndHasContent = shouldExpand && (expendedDescriptionHasValue || expandedCta)
  const { isBreakpoint: isBelowMd } = useWindowSize('md', '<')

  useEffect(() => {
    const el = ref.current

    if (!el) return

    const updateHeight = () => {
      const measuredHeight = el.offsetHeight
      setHeight(measuredHeight)
    }

    updateHeight()

    const observer = new ResizeObserver(() => {
      updateHeight()
    })

    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  const handleIsExpanded = () => {
    setIsExpanded(true)
  }

  return (
    <div className={styles['notification-banner-container']}>
      <div ref={ref} className={styles['notification-banner']}>
        <div className={styles['notification-banner__content']}>
          <div className={styles['notification-banner__title']}>
            <Paragraph text={title} fontSize={isBelowMd ? 'sm' : 'md'} noMarginBottom />
            {shouldExpandAndHasContent && (
              <button className={styles['notification-banner__expand-button']} onClick={() => handleIsExpanded()}>
                <ChevronDown />
              </button>
            )}
          </div>
        </div>
      </div>
      <div className={cx([styles['expanded-content-container'], styles[isExpanded ? 'is-open' : '']])}>
        <div className={styles.closeBtnContainer}>
          <button onClick={() => setIsExpanded(false)} className={styles.closeBtn}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles['expanded-content']}>
          <div className={styles['expanded-content__wrapper']}>
            <div className={styles['expanded-content__copy-container']}>
              <div className={styles['expanded-content__title']}>
                <Paragraph text={title} fontSize={isBelowMd ? 'md-plus' : 'lg'} />
              </div>
              {expandedDescription && (
                <div className={styles['expanded-content__description']}>
                  <Paragraph text={expandedDescription} fontSize={isBelowMd ? 'sm' : 'md'} />
                </div>
              )}
              {expandedCta && (
                <Button
                  {...expandedCta}
                  analytics={{
                    linkModule: 'notification_banner',
                    linkLocation: 'header'
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {isExpanded && <div className={styles.overlay} onClick={() => setIsExpanded(false)}></div>}
      {/* Spacer to push down page content to prevent overlapping. */}
      <div style={{ height: height }} />
    </div>
  )
}

export default NotificationBanner
